import { Box, IconButton } from '@youversion/design-system-components';
import CheckmarkStroked from '@youversion/design-system-icons/lib/CheckmarkStroked';
import XStroked from '@youversion/design-system-icons/lib/XStroked';
import Modal from 'components/ui/modal';
import ModalHeader from 'components/ui/modal/Header';
import { useQueryParams } from 'hooks/useQueryParams';
import { useSelectorEvents } from 'hooks/useSelectorEvents';
import { useTranslation } from 'next-i18next';
import React, { useEffect } from 'react';
import { titleize } from 'src/utils/helpers/titleize';
import { firebaseLogEvent } from 'utils/firebase';
import { useFrequency } from 'utils/store/frequency';

import GiveOptionListItem from '../controls/GiveOptionListItem';
import RadioListItem from '../controls/RadioListItem';
import ModalFooter from '../modal/Footer';

interface Props {
  onClose: () => void;
}

export default function FrequencyDialog({ onClose }: Props) {
  const { t } = useTranslation();
  const { hide_nav: hideNav } = useQueryParams();
  const { logSelectorDismissedEvent, logSelectorPresentedEvent } = useSelectorEvents('giving_frequency');
  const { recurringFrequencies, setFrequency, selectedFrequency } = useFrequency();

  useEffect(() => {
    firebaseLogEvent('GivingScreenView', { screen: 'giving_frequency' });
    logSelectorPresentedEvent(selectedFrequency);
    return () => void logSelectorDismissedEvent(selectedFrequency);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal aria-labelledby='dialog_modal_title_frequency' onClose={onClose}>
      <Box className='flex flex-col h-full'>
        <ModalHeader
          left={
            <IconButton className='inline-end-2 block-start-2' data-testid='close-modal-button' onClick={onClose}>
              <XStroked className='text-text-light dark:text-text-dark' />
              <label className='sr-only'>{t('giving.cta.close')}</label>
            </IconButton>
          }
          title={t('giving.titles.frequency')}
        />
        {hideNav ? (
          <>
            <Box className='grow overflow-x-auto pli-3'>
              {recurringFrequencies.map(item => {
                const isSelected = item.id === selectedFrequency;
                return item ? (
                  <GiveOptionListItem
                    className={isSelected ? 'bg-blue-10 dark:bg-gray-30' : ''}
                    key={item.id}
                    label={item.name}
                    onClick={() => {
                      firebaseLogEvent('GivingButtonTap', {
                        label: 'Frequency',
                        screen: 'giving_frequency',
                        value: item.id ?? '',
                      });
                      setFrequency(item.id);
                    }}
                    rightElement={
                      isSelected ? <CheckmarkStroked className='dark:text-text-dark text-text-light' /> : undefined
                    }
                    value={selectedFrequency}
                  />
                ) : null;
              })}
            </Box>
            <ModalFooter
              buttonText={t('giving.cta.done')}
              onClick={() => {
                firebaseLogEvent('GivingButtonTap', { label: 'Done', screen: 'giving_frequency' });
                onClose();
              }}
            />
          </>
        ) : (
          <>
            <Box className='flex flex-col gap-2 grow overflow-x-auto pli-2'>
              {recurringFrequencies.map(item => {
                const isSelected = item.id === selectedFrequency;
                return (
                  <RadioListItem
                    isChecked={isSelected}
                    key={item.id}
                    label={titleize(item.name)}
                    onChange={() => {
                      firebaseLogEvent('GivingButtonTap', {
                        label: 'Frequency',
                        screen: 'giving_frequency',
                        value: item.id,
                      });
                      setFrequency(item.id);
                    }}
                    value={selectedFrequency}
                  />
                );
              })}
            </Box>
            <ModalFooter
              buttonText={t('giving.cta.done')}
              onClick={() => {
                firebaseLogEvent('GivingButtonTap', { label: 'Done', screen: 'giving_frequency' });
                onClose();
              }}
            />
          </>
        )}
      </Box>
    </Modal>
  );
}
